import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { EmptyState } from '../components/EmptyState';
import { SearchField } from '../components/ListControls';
import { TabStrip } from '../components/TabStrip';
import { TitleBar } from '../components/TitleBar';
import { WithSideColumn } from '../components/WithSideColumn';
import { AlertsPanel } from '../features/alerts/AlertsPanel';
import type { NotifGroup } from '../features/notifications/build';
import { useNotifications } from '../features/notifications/useNotifications';
import { GROUPS } from '../features/notifications/groups';
import { NotificationRow } from '../features/notifications/NotificationList';
import {
  NotificationSettingsPanel,
  SummaryPanel,
  TodaysFocusPanel,
  UpcomingDeadlinesPanel,
} from '../features/notifications/NotificationPanels';
import { markRead, useReadIds } from '../features/notifications/readStore';
import { useReminders } from '../features/reminders/useReminders';
import { useSettings } from '../lib/settingsStore';

type Tab = 'All' | NotifGroup;
const TABS: Tab[] = ['All', 'Needs Attention', 'Updates', 'Reminders'];

/** The notification centre: what needs a decision, what is news, what is due. Alerts to your phone and Mac are set up on the right. */
export function NotificationsPage() {
  useSettings(); // re-draws when a kind of notification is switched on or off
  const reminders = useReminders().items;
  const readIds = useReadIds();
  const [tab, setTab] = useState<Tab>('All');
  const [search, setSearch] = useState('');

  const all = useNotifications();
  const w = search.trim().toLowerCase();
  const matches = (n: (typeof all)[number]) =>
    !w || [n.srb, n.title, n.text, n.category].join(' ').toLowerCase().includes(w);
  const count = (g: Tab) => all.filter((n) => g === 'All' || n.group === g).length;

  return (
    <Stack spacing={2}>
      <TitleBar title="Notifications" subtitle="Important updates and actions that need your attention.">
        <Button variant="outlined" color="inherit" onClick={() => markRead(all.map((n) => n.id))}>
          Mark all as read
        </Button>
      </TitleBar>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
      >
        <TabStrip tabs={TABS.map((t) => ({ value: t, label: `${t} (${count(t)})` }))} value={tab} onChange={setTab} />
        <SearchField
          placeholder="Search notifications…"
          value={search}
          onChange={setSearch}
          sx={{ maxWidth: { md: 320 } }}
        />
      </Stack>

      <WithSideColumn
        side={
          <>
            <SummaryPanel items={all} />
            <TodaysFocusPanel items={all} reminders={reminders} />
            <UpcomingDeadlinesPanel reminders={reminders} />
            <NotificationSettingsPanel />
            <AlertsPanel />
          </>
        }
      >
        {all.length === 0 && <EmptyState>Nothing needs you right now.</EmptyState>}
        {GROUPS.filter((g) => tab === 'All' || tab === g.group).map((g) => {
          const rows = all.filter((n) => n.group === g.group && matches(n));
          if (!rows.length) return null;
          return (
            <Stack key={g.group} spacing={1}>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                <Box
                  aria-hidden
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    color: '#fff',
                    fontWeight: 800,
                    bgcolor: `var(--mui-palette-${g.tone}-main)`,
                  }}
                >
                  {g.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" component="h2">
                    {g.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {g.subtitle}
                  </Typography>
                </Box>
                {tab === 'All' && (
                  <Button size="small" onClick={() => setTab(g.group)}>
                    View All ({count(g.group)})
                  </Button>
                )}
              </Stack>
              {rows.map((n) => (
                <NotificationRow key={n.id} n={n} read={readIds.includes(n.id)} />
              ))}
            </Stack>
          );
        })}
      </WithSideColumn>
    </Stack>
  );
}
